import { ResourceTypeId } from '~/client/src/shared/constants/resourceTypes'
import SortOrder from '~/client/src/shared/enums/SortOrder'

import {
  getSensorStateLabel,
  resolveSensorStateStatusBadge,
  resolveStruxTagSensorState,
} from './sensorState'
import { IResourceDefinition, IResourceFieldDefinition } from './types'

const getBatteryLevelLabel = (value: unknown): string => {
  const n = Number(value)
  if (Number.isNaN(n)) return 'Unknown'
  if (n <= 25) return 'Critical (0–25%)'
  if (n <= 50) return 'Low (26–50%)'
  if (n <= 75) return 'Medium (51–75%)'
  return 'Full (76–100%)'
}

// ID through StruxTag State lead the table and stay frozen; the rest follow in
// this order and are the only columns visible by default.
const STRUX_TAGS_FROZEN_COLUMNS = [
  'stxResourceId',
  'name',
  'battery',
  'equipment_name',
  'sensor_state',
]
const STRUX_TAGS_DEFAULT_COLUMNS = [
  ...STRUX_TAGS_FROZEN_COLUMNS,
  'last_telemetry_at',
  'latitude',
  'longitude',
  'temperature',
  'humidity',
]

const STRUXTAG_FIELD_DEFINITIONS: Record<string, IResourceFieldDefinition> = {
  serial: {
    dataType: 'text',
    card: {
      visible: true,
    },
    table: {
      searchable: true,
    },
  },
  battery: {
    dataType: 'number',
    format: ['round', 'percent'],
    card: {
      kind: 'metric',

      iconKey: 'battery',
      visible: true,
    },
    table: {
      sortable: true,
      groupable: true,
      filterable: true,
      searchable: true,
      labelKey: 'batteryLevel',
      semanticValueFn: getBatteryLevelLabel,
      semanticLabelFn: value => value,
    },
    edit: {
      readOnly: true,
    },
  },
  temperature: {
    dataType: 'number',
    format: ['round', 'temperatureUnitAware'],
    card: {
      kind: 'metric',

      iconKey: 'temperature',
      visible: true,
    },
    table: {
      searchable: true,
    },
    edit: {
      readOnly: true,
    },
  },
  humidity: {
    dataType: 'number',
    format: ['round', 'percent'],
    card: {
      kind: 'metric',

      iconKey: 'humidity',
      visible: true,
    },
    table: {
      searchable: true,
    },
    edit: {
      readOnly: true,
    },
  },
  sensor_state: {
    dataType: 'text',
    format: ['sensorStateLabel'],
    card: {
      isStatus: true,
      resolveStatusBadge: resolveSensorStateStatusBadge,
      visible: true,
    },
    table: {
      groupable: true,
      filterable: true,
      searchable: true,
      labelKey: 'struxTagState',
      semanticLabelFn: value => getSensorStateLabel(value),
    },
    edit: {
      readOnly: true,
    },
  },
  last_telemetry_at: {
    dataType: 'datetime',
    format: ['relativeTime'],
    card: {
      kind: 'metric',

      iconKey: 'telemetry',
      visible: true,
    },
    table: {
      searchable: true,
    },
    edit: {
      readOnly: true,
    },
  },
  equipment_name: {
    dataType: 'text',
    glyph: 'equipment',
    card: {
      kind: 'link',
      iconKey: 'equipment',
      visible: true,
      resolveLinkedEquipment: record => record.root?.linkedEquipment,
    },
    table: {
      searchable: true,
      sortable: true,
      groupable: true,
      labelKey: 'equipment',
    },
    edit: {
      readOnly: true,
    },
  },
  equipment_type: {
    dataType: 'text',
    card: {
      kind: 'text',
      visible: true,
    },
    table: {
      searchable: true,
    },
    edit: {
      readOnly: true,
    },
  },
  equipment_manufacturer: {
    dataType: 'text',
    card: {
      kind: 'text',
      visible: true,
    },
    table: {
      searchable: true,
    },
    edit: {
      readOnly: true,
    },
  },
  latitude: {
    dataType: 'number',
    card: {
      visible: false,
    },
    table: {},
    edit: {
      readOnly: true,
    },
  },
  longitude: {
    dataType: 'number',
    card: {
      visible: false,
    },
    table: {},
    edit: {
      readOnly: true,
    },
  },
}

export const struxTagsResourceDefinition: IResourceDefinition = {
  resourceTypeId: ResourceTypeId.SENSORS,
  fields: STRUXTAG_FIELD_DEFINITIONS,
  valueSources: {
    stxResourceId: { source: 'root' },
    infuseId: {
      source: 'root',
      path: 'infuseId',
    },
    sensor_state: {
      source: 'data',
      resolve: resolveStruxTagSensorState,
    },
    equipment_name: {
      source: 'root',
      path: 'linkedEquipment.name',
    },
    linked_equipment: {
      source: 'root',
      path: 'linkedEquipment.name',
    },
    equipment_type: {
      source: 'root',
      path: 'linkedEquipment.data.type',
    },
    equipment_manufacturer: {
      source: 'root',
      path: 'linkedEquipment.data.manufacturer',
    },
  },
  table: {
    columnOrder: STRUX_TAGS_DEFAULT_COLUMNS,
    defaultVisibleKeys: STRUX_TAGS_DEFAULT_COLUMNS,
    defaultFrozenKeys: STRUX_TAGS_FROZEN_COLUMNS,
  },
  card: {
    titleKey: 'name',
    subtitleKey: 'stxResourceId',
    // The card's meta rows, in order. sensor_state is absent on purpose: it drives the
    // status badge via resolveStatusBadge, not a meta row.
    defaultDisplayPropertyKeys: [
      'battery',
      'equipment_name',
      'last_telemetry_at',
      'temperature',
      'humidity',
    ],
  },
  map: {
    latitudeKey: 'latitude',
    longitudeKey: 'longitude',
  },
  ui: {
    noneGroupingKey: 'none',
    defaultGroupingKey: null,
    defaultSortKey: 'name',
    defaultSortOrder: SortOrder.ASC,
  },
}
